import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useArtistScopeSafe } from "@/lib/artistContext";

type NullableRecord = Record<string, unknown> | undefined;

export type BookingEventFormState = {
  artist_id: string;
  event_name: string;
  event_datetime: string;
  venue: string;
  city: string;
  state: string;
  country: string;
  ticket_url: string;
  contact_name: string;
  contact_email: string;
  supporting_acts_text: string;
  notes: string;
  guarantee: string;
  split_terms: string;
  artist_percentage: string;
  house_expense: string;
  sound_expense: string;
  lead_id?: number;
  /** Booking opportunity this show completes (idempotency + provenance key). */
  booking_lead_id?: number;
  metadata?: NullableRecord;
};

export type BookingEventSubmission = {
  artist_id: string;
  artist_name?: string;
  event_name?: string;
  event_date: string;
  venue: string;
  city?: string;
  state?: string;
  country?: string;
  ticket_url?: string;
  contact_name?: string;
  contact_email?: string;
  // Free-text names for now. Future: [{name, public_artist_id?, artist_id?}] so known
  // ArtistAscend artists link to their identity while unknown acts stay name-only.
  supporting_acts: string[];
  notes?: string;
  /** Always false: Bandsintown publishing has no working upload integration. */
  publish_upstream: false;
  metadata?: NullableRecord;
  lead_id?: number;
  booking_lead_id?: number;
  guarantee_amount?: number | null;
  split_terms?: string | null;
  artist_percentage?: number | null;
  house_expense?: number | null;
  sound_expense?: number | null;
};

type BookingEventFormProps = {
  initialValues?: Partial<BookingEventFormState>;
  submitting?: boolean;
  onSubmit: (values: BookingEventSubmission) => Promise<void>;
  submitLabel?: string;
  resetAfterSubmit?: boolean;
};

const DEFAULT_COUNTRY = "US";

function buildDefaultForm(artistId: string): BookingEventFormState {
  return {
    artist_id: artistId,
    event_name: "",
    event_datetime: formatDateTimeLocal(new Date()),
    venue: "",
    city: "",
    state: "",
    country: DEFAULT_COUNTRY,
    ticket_url: "",
    contact_name: "",
    contact_email: "",
    supporting_acts_text: "",
    notes: "",
    guarantee: "",
    split_terms: "",
    artist_percentage: "",
    house_expense: "",
    sound_expense: "",
  };
}

export function BookingEventForm({
  initialValues,
  submitting = false,
  onSubmit,
  submitLabel = "Create event",
  resetAfterSubmit = false,
}: BookingEventFormProps) {
  const { artists, selectedArtistId } = useArtistScopeSafe();
  const preferredArtistId = useMemo(
    () => initialValues?.artist_id || selectedArtistId || artists[0]?.id || "",
    [artists, initialValues?.artist_id, selectedArtistId],
  );
  const [form, setForm] = useState<BookingEventFormState>(() => buildDefaultForm(preferredArtistId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm((prev) => {
      const nextArtistId = prev.artist_id || preferredArtistId;
      if (nextArtistId === prev.artist_id) {
        return prev;
      }
      return { ...prev, artist_id: nextArtistId };
    });
  }, [preferredArtistId]);

  useEffect(() => {
    if (!initialValues) return;
    setForm((prev) => {
      const nextSupportingActs =
        typeof initialValues.supporting_acts_text === "string"
          ? initialValues.supporting_acts_text
          : Array.isArray((initialValues as unknown as { supporting_acts?: string[] }).supporting_acts)
            ? ((initialValues as unknown as { supporting_acts?: string[] }).supporting_acts ?? []).join(", ")
            : prev.supporting_acts_text;
      const nextArtistId = initialValues.artist_id || prev.artist_id || preferredArtistId;
      const nextEventDatetime =
        normaliseDateTime(initialValues.event_datetime) ?? prev.event_datetime;
      const nextCountry = initialValues.country || prev.country || DEFAULT_COUNTRY;
      const nextGuarantee = initialValues.guarantee ?? prev.guarantee;
      const nextSplitTerms = initialValues.split_terms ?? prev.split_terms;
      const nextArtistPercentage = initialValues.artist_percentage ?? prev.artist_percentage;
      const nextHouseExpense = initialValues.house_expense ?? prev.house_expense;
      const nextSoundExpense = initialValues.sound_expense ?? prev.sound_expense;
      // Undefined defaults (e.g. no known contact) keep the blank field rather than uncontrolling it.
      const provided = Object.fromEntries(
        Object.entries(initialValues).filter(([, value]) => value !== undefined),
      ) as Partial<BookingEventFormState>;
      const next = {
        ...prev,
        ...provided,
        artist_id: nextArtistId,
        event_datetime: nextEventDatetime,
        supporting_acts_text: nextSupportingActs,
        country: nextCountry,
        guarantee: nextGuarantee,
        split_terms: nextSplitTerms,
        artist_percentage: nextArtistPercentage,
        house_expense: nextHouseExpense,
        sound_expense: nextSoundExpense,
      };
      if (JSON.stringify(next) === JSON.stringify(prev)) {
        return prev;
      }
      return next;
    });
  }, [initialValues, preferredArtistId]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    try {
      const artistId = form.artist_id || preferredArtistId;
      if (!artistId) {
        setError("Select an artist before saving.");
        return;
      }
      const artistName = artists.find((item) => item.id === artistId)?.name;
      const supportingActs = form.supporting_acts_text
        ? form.supporting_acts_text
            .split(/[\n,]+/)
            .map((entry) => entry.trim())
            .filter(Boolean)
        : [];
      const submission: BookingEventSubmission = {
        artist_id: artistId,
        artist_name: artistName,
        event_name: form.event_name?.trim() || undefined,
        event_date: toIsoString(form.event_datetime),
        venue: form.venue.trim(),
        city: form.city?.trim() || undefined,
        state: form.state?.trim() || undefined,
        country: form.country?.trim() || DEFAULT_COUNTRY,
        ticket_url: form.ticket_url?.trim() || undefined,
        contact_name: form.contact_name?.trim() || undefined,
        contact_email: form.contact_email?.trim() || undefined,
        supporting_acts: supportingActs,
        notes: form.notes?.trim() || undefined,
        publish_upstream: false,
        metadata: form.metadata,
        lead_id: form.lead_id,
        booking_lead_id: form.booking_lead_id,
        guarantee_amount: normaliseCurrencyInput(form.guarantee),
        split_terms: form.split_terms?.trim() || null,
        artist_percentage: normalisePercentageInput(form.artist_percentage),
        house_expense: normaliseCurrencyInput(form.house_expense),
        sound_expense: normaliseCurrencyInput(form.sound_expense),
      };
      await onSubmit(submission);
      if (resetAfterSubmit) {
        setForm(buildDefaultForm(artistId));
      }
    } catch (err) {
      // Keep everything the user typed; show why it failed inside the drawer.
      setError(err instanceof Error && err.message ? err.message : "Unable to create event");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-xs uppercase tracking-wide text-muted-foreground">
          Artist
          <select
            className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            value={form.artist_id}
            onChange={(event) => setForm((prev) => ({ ...prev, artist_id: event.target.value }))}
            disabled={submitting}
          >
            <option value="">Select artist…</option>
            {artists.map((artist) => (
              <option key={artist.id} value={artist.id}>
                {artist.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs uppercase tracking-wide text-muted-foreground">
          Event title
          <input
            type="text"
            className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            placeholder="Spring Tour Kickoff"
            value={form.event_name}
            onChange={(event) => setForm((prev) => ({ ...prev, event_name: event.target.value }))}
            disabled={submitting}
          />
        </label>
        <label className="text-xs uppercase tracking-wide text-muted-foreground">
          Event datetime
          <input
            type="datetime-local"
            className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            value={form.event_datetime}
            onChange={(event) => setForm((prev) => ({ ...prev, event_datetime: event.target.value }))}
            disabled={submitting}
          />
        </label>
        <label className="text-xs uppercase tracking-wide text-muted-foreground">
          Venue
          <input
            type="text"
            className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            value={form.venue}
            onChange={(event) => setForm((prev) => ({ ...prev, venue: event.target.value }))}
            disabled={submitting}
            required
          />
        </label>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <label className="text-xs uppercase tracking-wide text-muted-foreground">
          City
          <input
            type="text"
            className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            value={form.city}
            onChange={(event) => setForm((prev) => ({ ...prev, city: event.target.value }))}
            disabled={submitting}
          />
        </label>
        <label className="text-xs uppercase tracking-wide text-muted-foreground">
          State / region
          <input
            type="text"
            className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            value={form.state}
            onChange={(event) => setForm((prev) => ({ ...prev, state: event.target.value }))}
            disabled={submitting}
          />
        </label>
        <label className="text-xs uppercase tracking-wide text-muted-foreground">
          Country
          <input
            type="text"
            className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            value={form.country}
            onChange={(event) => setForm((prev) => ({ ...prev, country: event.target.value }))}
            disabled={submitting}
          />
        </label>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-xs uppercase tracking-wide text-muted-foreground">
          Ticket / announce URL
          <input
            type="url"
            className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            value={form.ticket_url}
            onChange={(event) => setForm((prev) => ({ ...prev, ticket_url: event.target.value }))}
            disabled={submitting}
            placeholder="https://"
          />
        </label>
        <label className="text-xs uppercase tracking-wide text-muted-foreground">
          Supporting acts
          <input
            type="text"
            className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            value={form.supporting_acts_text}
            onChange={(event) => setForm((prev) => ({ ...prev, supporting_acts_text: event.target.value }))}
            disabled={submitting}
            placeholder="Comma separated list"
          />
        </label>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-xs uppercase tracking-wide text-muted-foreground">
          Contact name
          <input
            type="text"
            className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            value={form.contact_name}
            onChange={(event) => setForm((prev) => ({ ...prev, contact_name: event.target.value }))}
            disabled={submitting}
          />
        </label>
        <label className="text-xs uppercase tracking-wide text-muted-foreground">
          Contact email
          <input
            type="email"
            className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            value={form.contact_email}
            onChange={(event) => setForm((prev) => ({ ...prev, contact_email: event.target.value }))}
            disabled={submitting}
          />
        </label>
      </div>
      <label className="text-xs uppercase tracking-wide text-muted-foreground">
        Notes / rationale
        <textarea
          rows={4}
          className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
          value={form.notes}
          onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
          disabled={submitting}
        />
      </label>
      <div className="rounded-xl border border-border/60 bg-background/50 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary-foreground dark:text-amber-200">Deal terms (optional)</p>
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Guarantee
            <input
              type="text"
              className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              value={form.guarantee}
              onChange={(event) => setForm((prev) => ({ ...prev, guarantee: event.target.value }))}
              disabled={submitting}
              placeholder="$5,000"
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Split terms
            <input
              type="text"
              className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              value={form.split_terms}
              onChange={(event) => setForm((prev) => ({ ...prev, split_terms: event.target.value }))}
              disabled={submitting}
              placeholder="85/15 after expenses"
            />
          </label>
        </div>
        <div className="mt-3 grid gap-4 md:grid-cols-3">
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Percent to artist
            <input
              type="text"
              className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              value={form.artist_percentage}
              onChange={(event) => setForm((prev) => ({ ...prev, artist_percentage: event.target.value }))}
              disabled={submitting}
              placeholder="85%"
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            House expense
            <input
              type="text"
              className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              value={form.house_expense}
              onChange={(event) => setForm((prev) => ({ ...prev, house_expense: event.target.value }))}
              disabled={submitting}
              placeholder="$1,200"
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Sound expense
            <input
              type="text"
              className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              value={form.sound_expense}
              onChange={(event) => setForm((prev) => ({ ...prev, sound_expense: event.target.value }))}
              disabled={submitting}
              placeholder="$350"
            />
          </label>
        </div>
      </div>
      {error && (
        <p role="alert" className="text-sm text-rose-300">
          {error}
        </p>
      )}
      <Button type="submit" disabled={submitting || !form.venue.trim()} className="w-full justify-center md:w-auto">
        {submitting ? "Saving…" : submitLabel}
      </Button>
    </form>
  );
}

function formatDateTimeLocal(date: Date): string {
  const pad = (value: number) => value.toString().padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
}

function normaliseDateTime(value?: string): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return formatDateTimeLocal(parsed);
}

function toIsoString(value?: string): string {
  if (!value) return new Date().toISOString();
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString();
  }
  return parsed.toISOString();
}

function normaliseCurrencyInput(value: string): number | null {
  if (!value || !value.trim()) return null;
  const cleaned = value.replace(/[$,\s]/g, "");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalisePercentageInput(value: string): number | null {
  if (!value || !value.trim()) return null;
  const cleaned = value.replace(/[%\s]/g, "");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}
